import React, { useCallback, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../../defaultConfig';
import { useRestartStoppedPayment } from '../../../fetchHooks/usePaymentItems';
import type { Account, PaymentItem, PaymentItemDetails } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import TranslationsConfigType from '../../../translations/translationType';
import { EfakturaLink } from '../../EfakturaLink';
import UpdateFuturePaymentTransactionResult from '../../FuturePayment/UpdateFuturePaymentTransactionResult';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import ActionResultInfo from '../../shared/ActionResultInfo/ActionResultInfo';
import { DeletionStatus } from '../EFaktura/DeleteEFaktura';
import PaymentFields from '../Payment/PaymentFields';
import paymentReducer from '../Payment/form/reducer';
import useFormContext, { PaymentFormContext } from '../Payment/form/useFormContext';
import validate from '../Payment/form/validate';
import { PaymentRetryContext } from '../PaymentRetry';
import { SelectedReceiver } from '../SelectedReceiver';
import DeletePaymentItem from './DeletePaymentItem';
import createPaymentItemUpdateRequest from './createPaymentItemUpdateRequest';
import useHasChanged from './useHasChanged';

import './PaymentItemDetailsForm.scss';

interface ControllerProps {
    paymentDetails?: PaymentItemDetails;
    paymentItem: PaymentItem;
    toAccount?: SelectedReceiver;
    fromAccount?: Account;
    onClose: () => void;
}

type Props = {
    paymentDetails?: PaymentItemDetails;
    paymentItem: PaymentItem;
    onClose: () => void;
};

const getPaymentButtonText = (paymentItem: PaymentItem, translated: TranslationsConfigType) => {
    switch (paymentItem.paymentItemStatus) {
        case 'STOPPED':
        case 'REJECTED':
        case 'WAITING_FOR_SIGNING':
            return translated.tryAgain;
        case 'UNKNOWN':
        default:
            return translated.payment;
    }
};

const PaymentItemDetailsForm: React.FC<Props> = ({ paymentDetails, paymentItem, onClose }) => {
    const translated = useTranslation();

    const { featureToggles } = useConfig();
    const [deletionState, setDeletionState] = useState(DeletionStatus.INITIAL);
    const restartPayment = useRestartStoppedPayment();

    const {
        startSigning,
        signingError,
        signingStatus,
        signingOperationPending,
        handleCancelSigningFlow,
        showPaymentRetry,
    } = useSigningContext(createPaymentItemUpdateRequest());

    const { state, checkFormValidity } = useFormContext();

    const paymentInfo = paymentDetails || paymentItem;

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if ((await checkFormValidity()) && state.fromAccount) {
            startSigning({ paymentId: paymentInfo.paymentItemId, ...state });
        }
    };

    const handleRestartPaymentItem = useCallback(() => {
        restartPayment.mutate(paymentItem.paymentItemId);
    }, [paymentItem.paymentItemId, restartPayment]);

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const hasChanged = useHasChanged(paymentDetails);

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (signingError || signingStatus === 'COMPLETED') {
        return (
            <UpdateFuturePaymentTransactionResult
                paymentType="PAYMENT"
                paymentStatus={signingStatus}
                paymentError={signingError}
                retry={signingError ? handleCancelSigningFlow : handleRestartPaymentItem}
                onClose={onClose}
            />
        );
    }

    if (deletionState !== DeletionStatus.INITIAL) {
        return (
            <div className="delete-payment-item">
                <DeletePaymentItem
                    paymentItemId={paymentInfo.paymentItemId}
                    setDeleteStatus={setDeletionState}
                    deleteState={deletionState}
                />
            </div>
        );
    }

    if (restartPayment.isSuccess) {
        return (
            <ActionResultInfo
                title={translated.paymentItem.restartSuccess}
                retry={handleRestartPaymentItem}
                icon={
                    <CheckmarkAnimation
                        className="transfer-success-checkmark--centered"
                        checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE}
                    />
                }
            />
        );
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <div className="payment-item-details__title-wrapper">
                <h2>{translated.paymentItem.title}</h2>
                {!isMobile && (
                    <div className="payment-item-details__efaktura-link-wrapper">
                        <EfakturaLink eFakturaId={paymentDetails?.eFakturaId} />
                    </div>
                )}
            </div>

            <TransactionForm className="payment-item-details__form" onSubmit={onSubmit}>
                <PaymentFields
                    locked={!paymentItem.permissions?.canEdit || signingOperationPending}
                    isFuturePayment
                    lockReceiver
                    eFakturaId={paymentDetails?.eFakturaId}
                    lockNote={!!state.toAccount?.kidFormats?.length && !state.toAccount.kidRequired}
                />
                <TransactionSubmitWrapper>
                    {paymentItem.permissions?.canReject && (
                        <LinkButton
                            className="payment-item__delete-button"
                            icon={IconTypes.SLETT_24}
                            onClick={() => setDeletionState(DeletionStatus.CONFIRMING)}
                        >
                            {translated.delete}
                        </LinkButton>
                    )}
                    {paymentItem.permissions?.canRestart && !hasChanged && featureToggles.enableRestartPaymentItem && (
                        <ButtonWithSpinner
                            className="payment-item__restart-payment"
                            buttonType="positive"
                            showSpinner={restartPayment.isPending}
                            onClick={handleRestartPaymentItem}
                        >
                            {getPaymentButtonText(paymentItem, translated)}
                        </ButtonWithSpinner>
                    )}
                    {paymentItem.permissions?.canEdit && hasChanged && (
                        <ButtonWithSpinner
                            className="payment-item__update-btn"
                            buttonType="positive"
                            type="submit"
                            showSpinner={signingOperationPending}
                        >
                            {translated.recurringPayments.update}
                        </ButtonWithSpinner>
                    )}
                </TransactionSubmitWrapper>
            </TransactionForm>
        </SigningContextFlow>
    );
};

export default (props: ControllerProps) => {
    const paymentInfo = props.paymentDetails || props.paymentItem;
    return (
        <PaymentFormContext
            validateHook={validate}
            reducer={paymentReducer}
            initialState={{
                note: props.paymentDetails?.note ?? '',
                toAccount: props.toAccount,
                fromAccount: paymentInfo.from?.accountNumber,
                amount: Math.abs(paymentInfo.amount),
                currency: paymentInfo.currency ?? 'NOK',
                paymentDate: getZonedDate(paymentInfo.dueDate),
                showFromAccounts: false,
                acceptDuplicate: true,
            }}
        >
            <PaymentItemDetailsForm {...props} />
        </PaymentFormContext>
    );
};
